import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiCall } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

interface PartnerDetail {
  id: string;
  name: string;
  adapter_type: string;
  backend_url: string;
  enabled: boolean;
  tenants: Array<{ id: number; external_id: string; name: string; source_system: string; enabled: boolean }>;
  filters: Array<{ id: number; filter_type: string; filter_value: any; enabled: boolean }>;
}

interface ThroughputResponse {
  totals: { last_hour: number; last_day: number; last_week: number; total: number };
}

export const PartnerDashboard: React.FC = () => {
  const partnerId = useAuthStore((s) => s.partnerId);
  const [partner, setPartner] = useState<PartnerDetail | null>(null);
  const [throughput, setThroughput] = useState<ThroughputResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!partnerId) return;
    try {
      const [partnerResp, statsResp] = await Promise.all([
        apiCall(`/api/config/partners/${encodeURIComponent(partnerId)}`),
        apiCall(`/api/stats/throughput`),
      ]);
      if (!partnerResp.ok) throw new Error('Failed to load partner config');
      setPartner(await partnerResp.json());
      if (statsResp.ok) {
        setThroughput(await statsResp.json());
      }
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  }, [partnerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!partnerId) {
    return (
      <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3">
        Your account has no partner assignment.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Partner</h1>
        <p className="text-sm text-gray-500 mt-1">
          Read-only summary of your partner configuration. Filters can be edited; everything else is managed by ABENA.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      )}

      {partner ? (
        <>
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <div className="flex items-baseline justify-between">
              <h2 className="text-lg font-semibold text-gray-900">{partner.name}</h2>
              <span className="text-xs font-mono text-gray-400">{partner.id}</span>
            </div>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-wider">Adapter</dt>
                <dd className="text-gray-800">{partner.adapter_type}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-wider">Backend</dt>
                <dd className="text-gray-800 font-mono text-xs break-all">{partner.backend_url}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-wider">Enabled</dt>
                <dd className="text-gray-800">{partner.enabled ? 'Yes' : 'No'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-wider">Tenants</dt>
                <dd className="text-gray-800">{partner.tenants.length}</dd>
              </div>
            </dl>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {throughput?.totals && (
              <>
                <StatCard label="Events / hour" value={throughput.totals.last_hour} />
                <StatCard label="Events / day" value={throughput.totals.last_day} />
                <StatCard label="Events / week" value={throughput.totals.last_week} />
                <StatCard label="Events total" value={throughput.totals.total} />
              </>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Tenants assigned</h3>
            {partner.tenants.length === 0 ? (
              <p className="text-sm text-gray-400">No tenants assigned. Ask ABENA Ops to map customer organizations.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
                    <th className="py-2">Name</th>
                    <th className="py-2">External ID</th>
                    <th className="py-2">Source</th>
                    <th className="py-2">Enabled</th>
                  </tr>
                </thead>
                <tbody>
                  {partner.tenants.map((t) => (
                    <tr key={t.id} className="border-b border-gray-50 last:border-0">
                      <td className="py-2 font-medium text-gray-800">{t.name}</td>
                      <td className="py-2 text-gray-500 font-mono text-xs">{t.external_id}</td>
                      <td className="py-2 text-gray-500">{t.source_system}</td>
                      <td className="py-2">{t.enabled ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/partner/test-events"
              className="px-4 py-2 bg-[#1D407B] text-white text-sm font-medium rounded-lg hover:bg-[#162f5a] transition-colors no-underline"
            >
              Send test events
            </Link>
            <Link
              to="/eventlog"
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors no-underline"
            >
              View event log
            </Link>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-400">
          Loading partner details...
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
    <div className="text-2xl font-semibold text-gray-900 mt-1">{value.toLocaleString()}</div>
  </div>
);
